import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { isTeddyKey } from '../teddies.js'
import { MAX_LEVEL, MIN_LEVEL, STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { mastermindSkillOf } from './mastermind-skills.js'
import {
  DEFAULT_MASTERMIND_SIZE,
  GOES_ALLOWED,
  isFound,
  MASTERMIND_SHAPES,
  paletteFor,
  scoreGuess,
  secretFor,
  type MastermindSize,
  type Peg,
  type Score,
} from './mastermind-score.js'

/**
 * Chaka's Mastermind.
 *
 * A hidden row of teddies. She guesses, and each guess is answered with how
 * many are the right teddy in the right place and how many are the right teddy
 * in the wrong place. Ten goes to find it.
 *
 * What makes it worth building rather than being a second Riddles: Rocky hands
 * her every clue at once and asks her to reason from them. This one makes her
 * *choose what to find out next*, which is a different and harder skill — and
 * the one that transfers, because it is how you debug anything.
 *
 * Nothing here decides what an answer means. `mastermind-score.ts` does, and
 * it is the only thing that may.
 */

/** A guess she has made and what it was told back. */
export interface Played {
  readonly row: readonly Peg[]
  readonly score: Score
}

export const mastermindAction = z.discriminatedUnion('type', [
  /** Put a teddy in one place of the row she is building. */
  z.object({
    type: z.literal('place'),
    at: z.number().int().nonnegative(),
    peg: z.string().refine(isTeddyKey, 'not a teddy'),
  }),
  /** Take one back out. */
  z.object({ type: z.literal('take'), at: z.number().int().nonnegative() }),
  /** Clear the row she is building, without spending a go. */
  z.object({ type: z.literal('clear') }),
  /** Spend a go on the row as it stands. */
  z.object({ type: z.literal('try') }),
])

export type MastermindAction = z.infer<typeof mastermindAction>

export interface MastermindConfig {
  readonly size?: MastermindSize
  readonly levels?: SkillLevels
  readonly startedAt?: number
}

export interface MastermindState {
  readonly size: MastermindSize
  /** The hidden row. Never leaves this file except through `toMastermindView`. */
  readonly secret: readonly Peg[]
  readonly played: readonly Played[]
  /** The row being built. A hole she has not filled yet is null. */
  readonly building: readonly (Peg | null)[]
  readonly levels: SkillLevels
  readonly status: 'playing' | 'found' | 'out-of-goes'
  readonly turn: TurnState
  readonly startedAt: number
}

/**
 * What the browser is allowed to know.
 *
 * `answer` is null until the round is over, and that is the whole reason this
 * type exists. The hidden row lives in the state, the state lives in the tab,
 * and a view that carried it would put the answer one console line away — in a
 * game whose entire content is not knowing it. Maritza's Mosaic withholds its
 * picture the same way and for the same reason.
 */
export interface MastermindView {
  readonly size: MastermindSize
  readonly pegs: number
  readonly palette: readonly Peg[]
  readonly played: readonly Played[]
  readonly building: readonly (Peg | null)[]
  readonly goesLeft: number
  readonly status: MastermindState['status']
  /** The hidden row, once there is no longer any point in hiding it. */
  readonly answer: readonly Peg[] | null
}

export function toMastermindView(state: MastermindState): MastermindView {
  return {
    size: state.size,
    pegs: MASTERMIND_SHAPES[state.size].pegs,
    palette: paletteFor(state.size),
    played: state.played,
    building: state.building,
    goesLeft: GOES_ALLOWED - state.played.length,
    status: state.status,
    answer: state.status === 'playing' ? null : state.secret,
  }
}

/** Whether every place in the row she is building has a teddy in it. */
export function isRowFull(building: readonly (Peg | null)[]): building is readonly Peg[] {
  return building.every((peg) => peg !== null)
}

/**
 * Where she stands after this round.
 *
 * Up if she found it comfortably inside her goes, down only if she ran out
 * entirely, held otherwise — Stripey's `levelAfter` is the model, and so is
 * its reasoning: this moves once per round rather than once per guess, because
 * a guess is not an answer to be marked. A wrong guess is how the game is
 * played.
 *
 * "Comfortably" is two-thirds of the goes, which at ten is six. A child who
 * finds a five-peg row in six is reasoning rather than working through the
 * possibilities, and that is the thing getting better.
 */
function levelAfter(level: number, state: MastermindState): number {
  if (state.status === 'found') {
    return state.played.length * 3 <= GOES_ALLOWED * 2 ? Math.min(MAX_LEVEL, level + 1) : level
  }

  if (state.status === 'out-of-goes') return Math.max(MIN_LEVEL, level - 1)

  // Abandoned part-way. Nothing was learned about her either way, and taking a
  // level off somebody for closing a tab is the sort of thing that teaches her
  // not to open it again.
  return level
}

/**
 * The one skill this round touched, and where it now stands.
 *
 * Only the one, never the whole map — `saveSkillLevels` is explicit that
 * writing all of them back reverts progress made on another device.
 */
export function mastermindLevelsTouched(state: MastermindState): SkillLevels {
  const key = mastermindSkillOf(state.size).key

  return { [key]: levelAfter(state.levels[key] ?? STARTING_LEVEL, state) }
}

export interface MastermindAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

/**
 * One row for the whole round, not one per guess.
 *
 * A wrong guess here is not a wrong answer. It is the move — she plays one
 * deliberately to find out what it tells her, and a good player's early
 * guesses are *meant* to be wrong. Filing ten of them as failures would
 * describe the skill this game teaches as flailing, which is the argument
 * Maritza's Mosaic already makes about marking a square and changing your
 * mind, and it is sharper here.
 *
 * `expected` and `given` are integer columns, so as Stripey and Maritza both
 * do they carry the round's own numbers: the goes she had, and the goes she
 * used.
 */
export function mastermindAttempt(
  state: MastermindState,
  playerId: string,
  endedAt: number,
): MastermindAttempt {
  const { pegs, palette, repeats } = MASTERMIND_SHAPES[state.size]
  const how =
    state.status === 'found'
      ? `found in ${state.played.length}`
      : state.status === 'out-of-goes'
        ? 'not found'
        : `left after ${state.played.length}`

  return {
    skillKey: mastermindSkillOf(state.size).key,
    prompt: `A row of ${pegs} from ${palette} teddies, ${
      repeats ? 'repeats allowed' : 'none repeated'
    }, ${how}`,
    expected: GOES_ALLOWED,
    given: state.played.length,
    correct: state.status === 'found',
    elapsedMs: Math.max(0, endedAt - state.startedAt),
    playerId,
  }
}

export const mastermindEngine: GameEngine<MastermindConfig, MastermindState, MastermindAction> = {
  key: 'mastermind',
  actionSchema: mastermindAction,

  createInitialState(
    config: MastermindConfig,
    players: readonly Player[],
    rng: Rng,
  ): MastermindState {
    const size = config.size ?? DEFAULT_MASTERMIND_SIZE

    /*
     * The level is recorded and moves, but does not yet choose.
     *
     * At present the size *is* the difficulty: the three of them differ by peg
     * count and by whether a teddy may repeat, and those are steps rather than
     * a dial. Said plainly here rather than left as a silently unused argument
     * — when there is something to vary within a size, this is where it goes.
     */
    return {
      size,
      secret: secretFor(rng, size),
      played: [],
      building: new Array<Peg | null>(MASTERMIND_SHAPES[size].pegs).fill(null),
      levels: config.levels ?? {},
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt ?? 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    const { pegs } = MASTERMIND_SHAPES[state.size]
    const palette = paletteFor(state.size)

    if (action.type === 'clear') {
      // Nothing of hers in the row, so nothing to clear. Refused rather than
      // accepted as a no-op, so the move log does not fill from a held key.
      if (state.building.every((peg) => peg === null)) return reject(state, 'already-played')

      return accept({ ...state, building: new Array<Peg | null>(pegs).fill(null) })
    }

    if (action.type === 'try') {
      // Refused rather than answered: scoring a part-built row would mean
      // deciding what a hole counts as, and there is no honest answer.
      if (!isRowFull(state.building)) return reject(state, 'invalid-action')

      const score = scoreGuess(state.secret, state.building)
      const played = [...state.played, { row: state.building, score }]
      const found = isFound(score, pegs)

      return accept({
        ...state,
        played,
        building: new Array<Peg | null>(pegs).fill(null),
        status: found ? 'found' : played.length >= GOES_ALLOWED ? 'out-of-goes' : 'playing',
        turn: {
          ...state.turn,
          currentPlayerId:
            found || played.length >= GOES_ALLOWED ? null : state.turn.currentPlayerId,
        },
      })
    }

    if (action.at >= pegs) return reject(state, 'invalid-action')

    if (action.type === 'take') {
      if (state.building[action.at] === null) return reject(state, 'already-played')

      const building = [...state.building]
      building[action.at] = null

      return accept({ ...state, building })
    }

    // A teddy that is not in this size's palette. Reachable from a socket
    // message or a stale tab, not from the screen.
    if (!palette.includes(action.peg)) return reject(state, 'invalid-action')

    // Putting the same teddy back where it already is, which is a keyboard
    // repeating rather than a decision.
    if (state.building[action.at] === action.peg) return reject(state, 'already-played')

    const building = [...state.building]
    building[action.at] = action.peg

    return accept({ ...state, building })
  },

  isTerminal(state) {
    return state.status !== 'playing'
  },

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'mastermind',
      result:
        state.status === 'found' ? 'won' : state.status === 'out-of-goes' ? 'lost' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: mastermindSkillOf(state.size).label,
    }
  },
}
